import React, { Fragment } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';

import {
	SPOTLIGHT_ONBOARDING_STEP1_TARGET,
	SPOTLIGHT_ONBOARDING_STEP2_TARGET,
} from '../../../common/constants/spotlight';
import { useAvailableProducts } from '../../../controllers/store';
import { useSpotlightState } from '../../../controllers/store/spotlight';

import messages from './messages';

export const SearchPageOnboardingSpotlight = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [spotlight, { next, dismiss }] = useSpotlightState();

	const [availableProducts] = useAvailableProducts();

	const renderSpotlight = spotlight.state === 'visible' && availableProducts.length > 0; // Only render the spotlight after available products have been fetched

	if (!renderSpotlight) {
		return null;
	}

	const renderActiveSpotlight = () => {
		const spotlights = [
			<PopoverContent dismiss={dismiss} placement="right-end">
				<SpotlightCard testId={SPOTLIGHT_ONBOARDING_STEP1_TARGET}>
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(messages.onboardingStep1Heading)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={dismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							{formatMessage(
								fg('product-terminology-refresh')
									? messages.onboardingStep1DescriptionAppify
									: messages.onboardingStep1Description,
							)}
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>
							{formatMessage(messages.onboardingStep1Progress)}
						</SpotlightStepCount>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={next}>
								{formatMessage(messages.next)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>,
			<PopoverContent dismiss={dismiss} placement="right-end">
				<SpotlightCard testId={SPOTLIGHT_ONBOARDING_STEP2_TARGET}>
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(messages.onboardingStep2Heading)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={dismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							{formatMessage(
								fg('product-terminology-refresh')
									? messages.onboardingStep2DescriptionAppify
									: messages.onboardingStep2Description,
							)}
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>
							{formatMessage(messages.onboardingStep2Progress)}
						</SpotlightStepCount>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={dismiss}>
								{formatMessage(messages.done)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>,
		];

		return spotlights[spotlight.activeSpotlight];
	};

	return <Fragment>{renderActiveSpotlight()}</Fragment>;
};
